/**
 * Native save-offer rescue: client side of `record-native-save-offer`.
 *
 * The recorder is called from inside the native checkout right after the
 * store switch succeeds, using the snapshot captured BEFORE the purchase
 * (previous product, store). If that call fails, the payload is parked in
 * sessionStorage and the rescue sheet drains it on the post-checkout reload.
 * See docs/trip/plans/F_2026-08-16_mobile-save-offer-rescue.plan.md items 5, 7, 8.
 */
import { supabase } from '@/integrations/supabase/client';
import type { CancellationReason } from '@/components/composites/CancelFlowModal';

// The permanent $49.99 annual cancellation rescue. It is recorded only after
// an eligible subscriber explicitly accepts the offer in the cancel flow.
export const ANNUAL_4999_OFFER_KEY = 'annual_4999';
// iOS monthly members on "too expensive": Apple promo monthly_save_699x3.
export const MONTHLY_699X3_OFFER_KEY = 'monthly_699x3';
export type NativeSaveOfferKey = typeof ANNUAL_4999_OFFER_KEY | typeof MONTHLY_699X3_OFFER_KEY;
export const NATIVE_RESCUE_PENDING_RECORD_KEY = 'jp.rescue.pending_record';

export type NativeRescueStore = 'app_store' | 'play_store';

export interface RecordNativeSaveOfferInput {
  /** Omitted by records parked before the monthly offer existed: annual. */
  offerKey?: NativeSaveOfferKey;
  store: NativeRescueStore;
  previousProductId: string;
  nativeProductId: string;
  pitchId?: string | null;
  cancelReason?: CancellationReason | null;
}

export interface RecordNativeSaveOfferResponse {
  status: 'recorded' | 'already_recorded';
  confirmed: boolean;
}

function toBody(input: RecordNativeSaveOfferInput): Record<string, unknown> {
  return {
    offer_key: input.offerKey ?? ANNUAL_4999_OFFER_KEY,
    store: input.store,
    previous_product_id: input.previousProductId,
    native_product_id: input.nativeProductId,
    ...(input.pitchId ? { pitch_id: input.pitchId } : {}),
    ...(input.cancelReason ? { cancel_reason: input.cancelReason } : {}),
  };
}

export async function recordNativeSaveOffer(
  input: RecordNativeSaveOfferInput
): Promise<RecordNativeSaveOfferResponse> {
  const { data, error } = await supabase.functions.invoke<RecordNativeSaveOfferResponse>(
    'record-native-save-offer',
    { body: toBody(input) }
  );
  if (error) {
    throw new Error(error.message || 'Could not record the save offer.');
  }
  if (!data || (data.status !== 'recorded' && data.status !== 'already_recorded')) {
    throw new Error('Unexpected response from record-native-save-offer.');
  }
  return data;
}

function safeSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Park a failed record so the sheet can retry once after the reload. */
export function savePendingNativeRescueRecord(input: RecordNativeSaveOfferInput): void {
  const storage = safeSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(NATIVE_RESCUE_PENDING_RECORD_KEY, JSON.stringify(input));
  } catch {
    // Storage full or blocked: the webhook still confirms the switch server-side.
  }
}

/** Read and clear the parked record (single retry). */
export function takePendingNativeRescueRecord(): RecordNativeSaveOfferInput | null {
  const storage = safeSessionStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(NATIVE_RESCUE_PENDING_RECORD_KEY);
    storage.removeItem(NATIVE_RESCUE_PENDING_RECORD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RecordNativeSaveOfferInput>;
    if (
      (parsed.store !== 'app_store' && parsed.store !== 'play_store') ||
      typeof parsed.previousProductId !== 'string' ||
      typeof parsed.nativeProductId !== 'string'
    ) {
      return null;
    }
    return {
      offerKey:
        parsed.offerKey === MONTHLY_699X3_OFFER_KEY
          ? MONTHLY_699X3_OFFER_KEY
          : ANNUAL_4999_OFFER_KEY,
      store: parsed.store,
      previousProductId: parsed.previousProductId,
      nativeProductId: parsed.nativeProductId,
      pitchId: typeof parsed.pitchId === 'string' ? parsed.pitchId : null,
      cancelReason: (parsed.cancelReason as CancellationReason | undefined) ?? null,
    };
  } catch {
    return null;
  }
}
